/*
  ATHLO+ — HTML5 canvas image flattener  →  window.HubImageFlatten
  ===========================================================================
  Shopify's native file uploader can't force-flatten a transparent PNG, so a
  user can upload a transparent image that renders unpredictably on dark/light
  surfaces. This flattens EVERY upload onto a solid background, force-crops to a
  target aspect, and compresses to WebP under a size cap BEFORE it's saved.

  API
    HubImageFlatten.process(file, {
      bg:'#0e1116',     // solid fill under transparency
      aspect:1,         // target w/h (1 = square). null = keep source aspect
      max:1024,         // max output edge in px
      maxBytes:2097152, // hard size cap (default 2MB) — quality steps down to hit it
      type:'image/webp' // output mime (falls back to jpeg if webp unsupported)
    }) -> Promise<{ blob, dataUrl, width, height, bytes, type }>

  Rejects non-images and files that can't be decoded. All work is client-side.
*/
(function () {
  'use strict';
  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      if (!file || !/^image\//.test(file.type || '')) { reject(new Error('not an image')); return; }
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
      img.src = url;
    });
  }
  function supportsWebp() {
    try { return document.createElement('canvas').toDataURL('image/webp').indexOf('image/webp') === 5; } catch (e) { return false; }
  }
  function canvasToBlob(canvas, type, q) {
    return new Promise(function (resolve) {
      if (canvas.toBlob) canvas.toBlob(function (b) { resolve(b); }, type, q);
      else { // Safari fallback
        var d = canvas.toDataURL(type, q), bin = atob(d.split(',')[1]), arr = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(new Blob([arr], { type: type }));
      }
    });
  }
  function process(file, opts) {
    opts = opts || {};
    var bg = opts.bg || '#0e1116';
    var aspect = opts.aspect === null ? null : (opts.aspect || 1);
    var max = opts.max || 1024;
    var maxBytes = opts.maxBytes || 2 * 1024 * 1024;
    var type = opts.type || 'image/webp';
    if (type === 'image/webp' && !supportsWebp()) type = 'image/jpeg';
    return loadImage(file).then(function (img) {
      var sw = img.naturalWidth, sh = img.naturalHeight;
      // target size + centre-crop rect
      var tw, th, sx = 0, sy = 0, sCropW = sw, sCropH = sh;
      if (aspect) {
        var srcAspect = sw / sh;
        if (srcAspect > aspect) { sCropW = Math.round(sh * aspect); sx = Math.round((sw - sCropW) / 2); }
        else { sCropH = Math.round(sw / aspect); sy = Math.round((sh - sCropH) / 2); }
        th = Math.min(max, sCropH); tw = Math.round(th * aspect);
      } else {
        var scale = Math.min(1, max / Math.max(sw, sh)); tw = Math.round(sw * scale); th = Math.round(sh * scale);
      }
      var canvas = document.createElement('canvas'); canvas.width = tw; canvas.height = th;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, tw, th);          // FLATTEN: solid bg first
      ctx.drawImage(img, sx, sy, sCropW, sCropH, 0, 0, tw, th); // then the (cropped) image
      // step quality down until under the byte cap
      var q = 0.92;
      function attempt() {
        return canvasToBlob(canvas, type, q).then(function (blob) {
          if (blob.size <= maxBytes || q <= 0.4) {
            return new Promise(function (res) {
              var fr = new FileReader();
              fr.onload = function () { res({ blob: blob, dataUrl: fr.result, width: tw, height: th, bytes: blob.size, type: type }); };
              fr.readAsDataURL(blob);
            });
          }
          q -= 0.12; return attempt();
        });
      }
      return attempt();
    });
  }
  window.HubImageFlatten = { process: process, supportsWebp: supportsWebp };
})();
